import { lazy, Suspense } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ADMIN_SECTIONS, type AdminSection } from "@/lib/admin-authz";
import { reportSearch } from "@/lib/admin/report-range";

// The admin panel is ~8k lines of dashboard code. Load it only when an
// authorised admin actually opens /admin — storefront visitors never fetch it.
const AdminPanel = lazy(() =>
  import("@/components/admin/AdminPanel").then((m) => ({ default: m.AdminGate })),
);

function AdminRouteComponent() {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen bg-[#F7F7F5] text-[#2C2C2C] flex items-center justify-center">
          <p className="text-sm text-[#2C2C2C]/60">Loading…</p>
        </div>
      }
    >
      <AdminPanel />
    </Suspense>
  );
}

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminRouteComponent,
  validateSearch: (search: Record<string, unknown>): {
    section?: AdminSection | "reports";
    report?: string;
    reportFrom?: string;
    reportTo?: string;
    reportGroup?: "day" | "month";
    orderId?: string;
    page?: number;
    webhookId?: string;
    reviewId?: string;
    emailLogId?: string;
    customerRef?: string;
    ticketId?: string;
    view?: string;
    q?: string;
    leadsView?: "contacts" | "checkouts" | "journeys" | "partners";
  } => {
    const raw = typeof search.section === "string" ? search.section : "";
    const section =
      raw === "reports"
        ? ("reports" as const)
        : (ADMIN_SECTIONS as readonly string[]).includes(raw)
          ? (raw as AdminSection)
          : undefined;
    const str = (key: string): string | undefined => {
      const v = search[key];
      return typeof v === "string" && v ? v.slice(0, 120) : undefined;
    };
    return {
      ...reportSearch(search),
      page: Number.isInteger(Number(search.page)) ? Math.max(0, Number(search.page)) : 0,
      ...(["contacts", "checkouts", "journeys", "partners"].includes(String(search.leadsView)) ? { leadsView: search.leadsView as "contacts" | "checkouts" | "journeys" | "partners" } : {}),
      ...(section ? { section } : {}),
      ...(str("report") ? { report: str("report") } : {}),
      ...(str("orderId") ? { orderId: str("orderId") } : {}),
      ...(str("reviewId") ? { reviewId: str("reviewId") } : {}),
      ...(str("emailLogId") ? { emailLogId: str("emailLogId") } : {}),
      ...(str("webhookId") ? { webhookId: str("webhookId") } : {}),
      ...(str("customerRef") ? { customerRef: str("customerRef") } : {}),
      ...(str("ticketId") ? { ticketId: str("ticketId") } : {}),
      ...(str("view") ? { view: str("view") } : {}),
      ...(str("q") ? { q: str("q") } : {}),
    };
  },


  head: () => ({
    meta: [
      { title: "Admin — TruWe" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async ({ context }) => {
    // Client-only (parent layout is ssr:false). Force the admin panel to be
    // served ONLY from admin.truwe.in — anywhere else redirects to the
    // canonical host, preserving the /admin path.
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      const isLocalPreview =
        host === "localhost" ||
        host.endsWith(".lovable.app") ||
        host.endsWith(".lovable.dev") ||
        host.endsWith(".lovableproject.com");
      if (host !== "admin.truwe.in" && !isLocalPreview) {
        window.location.replace("https://admin.truwe.in/admin");
        // Throw to halt the loader chain while the browser navigates away.
        throw new Error("redirecting_to_admin_host");
      }
    }
    // Access was already resolved by the _authenticated layout; reuse it
    // instead of a second round-trip. Non-admins go back to /account.
    const parent = context as { access?: { roles: string[]; sections: AdminSection[] } };
    const access = parent.access ?? { roles: [], sections: [] };
    if (!access.sections.length) {
      throw redirect({ to: "/account" });
    }
    return { adminAccess: access };
  },
});

